import React, { useMemo } from 'react';
import { getHorizontalWidth } from '@components/Widgets/layout.components';
import { ChartClickInteraction } from '@components/Widgets/types';
import { FeatureFlags, FormatterType, isValidSeverity, SeverityPrimaryColors } from '@fabric-ui/components';
import { generateInnerDefaultFormatter } from '@fabric-ui/components/Table';
import { uniqBy } from '@fabric-ui/utils';
import {
  getDateBucketFromExpression,
  getFieldTitleByExpression,
  getFieldTypeByExpression,
  isDateTruncExpression,
} from '@views/CustomDashboards/CustomDashboardsUtils/expressionUtils';
import { LayoutType } from 'recharts/types/util/types';
import { AvComposedWidget, type AvComposedWidgetProps } from '../../../../../components/Widgets/AvComposedWidget';
import { WidgetTooltipContent } from '../../../../../components/Widgets/WidgetTooltip/WidgetTooltip';
import { useAvContext } from '../../../../../context/AvContextProvider';
import { useMetricsContextAsObject } from '../../../../../hooks/useMetricsContext';
import { useFormattingRules } from '../../../../FormattingRules/hooks';
import { FormattingRule } from '../../../../FormattingRules/types';
import {
  getColorFromFormattingConditions,
  getFormattingRuleById,
  isDimensionFormattingRule,
  NONE_ID,
} from '../../../../FormattingRules/utils';
import { Measurement } from '../../../../Measurements/types';
import { NO_VALUE, orientationOption, OrientationType, stackedOption, StackedType } from '../../../constants';
import { getRepeatedValueToString } from '../../../CustomDashboardsUtils';
import { hasTimeBucket } from '../../../EditCustomDashboard/components/TimeBucketBreakdown/utils';
import { getDateFormatFromWidget } from '../../../EditCustomDashboard/utils';
import { isWidgetDrilldownActive } from '../../../EditCustomDashboard/widget-clickthrough.utils';
import { getCustomTickFormatter, useCustomPalette } from '../../../hooks';
import { useGetDisplayName } from '../../../hooks/useGetDisplayName';
import { useGetFieldType } from '../../../hooks/useGetFieldType';
import {
  AllTypeWidgets,
  BarCategoryWidgetTypes,
  BarLegendPosition,
  BarSubType,
  Data,
  DisplayModeOptions,
  SortBy,
  SortDir,
} from '../../../types';
import { AxisTickType, LabelFormatterOption } from '../../../types/types';
import { BarVisualizationType, getBarQueryType, getBarWidgetTooltipContentProps } from './BarWidget.utils';

type BarDataEntry = {
  headCellType?: string;
  name: string;
  originalValue: unknown;
  [key: string]: unknown;
};

export interface BarWidgetProps {
  widget: BarCategoryWidgetTypes;
  data?: Record<string, any>[];
  selected?: any[];
  onSelect?: (...v: any[]) => void;
  clickInteractions?: ChartClickInteraction[];
}

type BarWidgetImplProps = BarWidgetProps & {
  formattingRules?: ReturnType<typeof useFormattingRules>['data'];
  measurementsObject?: Record<string, Measurement>;
  featureFlags?: ReturnType<typeof useAvContext>['featureFlags'];
  metricDisplayName?: string | number | null | undefined;
  getHeadCellType: ReturnType<typeof useGetFieldType>;
  getDisplayName: ReturnType<typeof useGetDisplayName>;
  fieldTypeMap: Record<string, any>;
};

export const BarWidgetImpl = (props: BarWidgetImplProps) => {
  const {
    getHeadCellType,
    getDisplayName,
    widget,
    clickInteractions,
    data,
    featureFlags = {},
    formattingRules,
    onSelect,
    selected,
    measurementsObject = {},
    fieldTypeMap,
  } = props;
  const customPalette = useCustomPalette(data);
  const {
    barData,
    series,
    isStacked,
    hasFormattingRules,
    tooltipProps,
    tooltipComponentProps,
    getCellColorFromData,
    activeDimWithExpression,
    barQueryType,
  } = useFormatBarData({
    data,
    widget,
    customPalette,
    getDisplayName,
    getHeadCellType,
    isActiveIndex: !!widget.drillDownHierarchy?.activeIndex,
    sortBy: widget.requests?.[0].sorting?.[0] || [],
    formattingRules,
  });

  const {
    customLabels,
    hideLabel,
    labelFormatter,
    legend: { margin },
    xAxisTickType,
    yAxisTickType,
  } = widget.definition.custom;

  const horizontalWidth = useMemo(() => getHorizontalWidth(barData), [barData]);
  const shouldRotateTicks = (!xAxisTickType || xAxisTickType === AxisTickType.Dynamic) && barData.length > 6;
  const isVertical = orientationOption[widget.type] === OrientationType.Vertical;
  const rotation =
    xAxisTickType && xAxisTickType !== AxisTickType.Dynamic
      ? xAxisTickType
      : shouldRotateTicks && widget.definition.custom.labelFormatter !== LabelFormatterOption.SOURCE_ICONS
        ? AxisTickType.Slanted
        : AxisTickType.Horizontal;

  const isMultiIcons = series.some(item => item.dataKey.toString().split(',').length > 1);
  const dimNameOrAlias = getActiveDimNameOrAlias(widget);
  const displayName = activeDimWithExpression ? getDisplayName(activeDimWithExpression.name) : getDisplayName(dimNameOrAlias);
  const isScroll = widget.definition.custom.displayMode === DisplayModeOptions.SCROLL;

  const customTickFormatter = getCustomTickFormatter({
    labelFormattingOption: labelFormatter,
    rotation,
    isVertical,
    sx: { mt: isVertical ? 2 : 0, mr: '2px' },
    itemMaxWidth: rotation === AxisTickType.Vertical ? 80 : undefined,
  });

  const labels: AvComposedWidgetProps['labels'] = {
    ...(!hideLabel
      ? isVertical
        ? {
            x: activeDimWithExpression
              ? getFieldTitleByExpression({ expression: activeDimWithExpression.expression, displayName })
              : displayName,
          }
        : {
            y: activeDimWithExpression
              ? getFieldTitleByExpression({ expression: activeDimWithExpression.expression, displayName })
              : displayName,
          }
      : {}),
    ...customLabels,
  };

  const chartProps: AvComposedWidgetProps['chartProps'] = {
    margin: margin || { top: 0, bottom: isVertical ? 30 : 10, left: isVertical ? 0 : horizontalWidth },
    layout: (isVertical ? 'horizontal' : 'vertical') as LayoutType,
    xAxisTickType: shouldRotateTicks ? AxisTickType.Slanted : xAxisTickType,
    yAxisTickType: !yAxisTickType || yAxisTickType === AxisTickType.Dynamic ? AxisTickType.Horizontal : yAxisTickType,
  };

  const defaultAxisProps = {
    x: { customTickFormatter } as NonNullable<NonNullable<AvComposedWidgetProps['defaultAxisProps']>['x']>,
    y: {} as NonNullable<NonNullable<AvComposedWidgetProps['defaultAxisProps']>['y']>,
    xLabel: {},
  } as const satisfies AvComposedWidgetProps['defaultAxisProps'];

  if (widget.definition.custom.labelFormatter === LabelFormatterOption.SOURCE_ICONS && !isVertical) {
    defaultAxisProps.y.width = 75;
  }

  if (widget.definition.custom.labelFormatter === LabelFormatterOption.SOURCE_ICONS_WITH_NAMES && !isVertical && isMultiIcons) {
    defaultAxisProps.y.width = 134;
  }

  if (isScroll && isVertical) {
    defaultAxisProps.x.interval = 0;
  }

  if (isScroll && !isVertical) {
    defaultAxisProps.x.interval = 0;
  }

  if (widget.definition.custom.labelFormatter === LabelFormatterOption.SOURCE_ICONS && isVertical && labels.x) {
    defaultAxisProps.x.height = 55;
  }

  if (widget.definition.custom.scale) {
    if (isVertical) {
      defaultAxisProps.y.scale = widget.definition.custom.scale;
    } else {
      defaultAxisProps.x.scale = widget.definition.custom.scale;
    }
  }

  const responsiveContainerProps: AvComposedWidgetProps['responsiveContainerProps'] = isScroll
    ? {
        width: isVertical && barData.length > 11 ? `${barData.length * 8}%` : '100%',
        height: !isVertical && barData.length > 6 ? `${barData.length * 8}%` : '100%',
        sx: {
          overflowX: isVertical ? 'auto' : 'hidden',
          overflowY: !isVertical ? 'auto' : 'hidden',
        },
      }
    : {};

  if (
    widget.definition.custom.labelFormatter === LabelFormatterOption.SOURCE_ICONS &&
    isVertical &&
    xAxisTickType === AxisTickType.Horizontal
  ) {
    chartProps.xAxisTickType = AxisTickType.Dynamic;
  }

  const sx: AvComposedWidgetProps['sx'] = {
    pb: 1,
    pt: '10px',
  };

  const showDataLabels =
    featureFlags[FeatureFlags.WidgetShowDataLabels] &&
    !!widget.definition.custom.showDataLabels &&
    (widget.type === BarSubType.BarHorizontal || widget.type === BarSubType.BarVertical);

  const legendPosition: AvComposedWidgetProps['legendPosition'] =
    widget.definition.custom.legend.position === BarLegendPosition.Bottom ? 'bottom' : 'top';

  return (
    <AvComposedWidget
      showDataLabels={showDataLabels}
      tooltipComponentProps={tooltipComponentProps}
      getFormattedColorCell={getCellColorFromData}
      dateFormat={getDateFormatFromWidget(widget)}
      clickInteractions={clickInteractions}
      className="draggableCancel"
      series={series}
      data={barData}
      defaultAxisProps={defaultAxisProps}
      labels={labels}
      xAxisKey="name"
      onSelect={onSelect}
      selected={selected}
      showLegend={widget.definition.custom.legend.position !== BarLegendPosition.None}
      legendPosition={legendPosition}
      responsiveContainerProps={responsiveContainerProps}
      chartProps={chartProps}
      sx={sx}
      isStacked={isStacked}
      hasFormattingRules={hasFormattingRules}
      tooltipProps={{
        ...tooltipProps,
        widgetData: barData,
      }}
      getTooltipContent={
        featureFlags[FeatureFlags.NewChartTooltip]
          ? // eslint-disable-next-line react/no-unstable-nested-components
            ({ content: _, ...props }) => (
              <WidgetTooltipContent
                interactions={widgetTooltipProps => {
                  const { payload, activeCellDataKey } = widgetTooltipProps || {};
                  if (!payload) {
                    return [];
                  }

                  if (!activeCellDataKey && payload?.length > 1 && barQueryType === BarVisualizationType.MultiMetric) {
                    const fields = [widget.requests[0].select.dims[0], ...(widget.drillDownHierarchy?.fields || [])];
                    const activeIndex = widget.drillDownHierarchy?.activeIndex || 0;
                    const hasNextDrilldown = fields.length && Boolean(fields[activeIndex + 1]);
                    if (!hasNextDrilldown) {
                      return [];
                    }
                    return clickInteractions?.filter(intr => intr.title !== 'View Items') || [];
                  }

                  if (!activeCellDataKey) {
                    return [];
                  }

                  return (
                    clickInteractions?.filter(v => v.isEnabledForCell({ dataKey: payload?.[0]?.dataKey, ...payload?.[0]?.payload })) || []
                  );
                }}
                widgetData={barData}
                {...props}
                {...getBarWidgetTooltipContentProps({
                  measurements: measurementsObject,
                  widget,
                  tooltipProps,
                  fieldTypeMap,
                  widgetData: barData,
                  ...props,
                })}
              />
            )
          : undefined
      }
      enableDefaultMinPointSize={featureFlags[FeatureFlags.DefaultMinPointSizeBarChart]}
    />
  );
};

const BarWidget = ({ widget, data, selected, onSelect, clickInteractions }: BarWidgetProps) => {
  const {
    projectionId: { name: projectionName },
  } = widget.requests[0];
  const {
    featureFlags,
    accountEntities: { fieldTypeMap },
  } = useAvContext();
  const measurementsObject = useMetricsContextAsObject();
  const getDisplayName = useGetDisplayName(projectionName);
  const getHeadCellType = useGetFieldType(projectionName);

  const { data: formattingRules = [] } = useFormattingRules();

  return (
    <BarWidgetImpl
      getDisplayName={getDisplayName}
      getHeadCellType={getHeadCellType}
      widget={widget}
      clickInteractions={clickInteractions}
      data={data}
      featureFlags={featureFlags}
      formattingRules={formattingRules}
      measurementsObject={measurementsObject}
      fieldTypeMap={fieldTypeMap}
      onSelect={onSelect}
      selected={selected}
    />
  );
};

export default BarWidget;

const getActiveDimNameOrAlias = (widget: AllTypeWidgets) => {
  if (!isWidgetDrilldownActive(widget)) {
    return widget.requests[0].select.dims[0].alias || widget.requests[0].select.dims[0].name;
  }
  const { activeIndex, fields } = widget.drillDownHierarchy;

  return fields[activeIndex - 1]?.alias || fields[activeIndex - 1]?.name;
};

const useFormatBarData = ({
  data = [],
  widget,
  customPalette,
  getDisplayName,
  getHeadCellType,
  isActiveIndex = false,
  sortBy,
  formattingRules = [],
}: {
  data?: Array<Record<PropertyKey, Data>>;
  widget: BarCategoryWidgetTypes;
  customPalette: string[];
  getDisplayName: ReturnType<typeof useGetDisplayName>;
  getHeadCellType: ReturnType<typeof useGetFieldType>;
  isActiveIndex: boolean;
  sortBy: SortBy;
  formattingRules?: FormattingRule[];
}) => {
  const { dims, metrics } = widget.requests[0].select;
  const dimName = widget.requests[0].select.dims[0].name;
  const dimNameOrAlias = getActiveDimNameOrAlias(widget);
  const barQueryType = getBarQueryType({ dims, metrics });
  const isStacked = stackedOption[widget.type] === StackedType.Stacked;
  const isStacked100 = stackedOption[widget.type] === StackedType.Stacked100;
  const isOneBarForXValue = isStacked || isStacked100 || barQueryType === BarVisualizationType.Normal;
  const relevantDim = barQueryType === BarVisualizationType.Normal || isActiveIndex ? getActiveDimNameOrAlias(widget) : dims[1]?.name;
  const isMultiDimBarType = barQueryType === BarVisualizationType.MultiDims;
  const { featureFlags } = useAvContext();

  const singleMetricName = metrics[0].alias!;
  const shouldDisplayNull = true;

  const dimsWithExpressions = useMemo(
    () =>
      widget.requests[0].select.dims.reduce((acc, dim) => {
        if (dim.expression) {
          return { ...acc, [dim.name]: dim };
        }
        return acc;
      }, {}),
    [widget.requests[0].select.dims]
  );
  const activeDimWithExpression = dimsWithExpressions[dimName];
  const primaryDimKey = activeDimWithExpression ? dimName : dimNameOrAlias;

  const dimFormattingRule = getFormattingRuleById(
    widget.definition.custom.fieldStyle?.[primaryDimKey]?.formattingRuleId,
    formattingRules,
    widget?.definition.custom.applyFormattingToAll?.formattingRuleId
  );

  const xAxis0Values = uniqBy(
    data?.map(v => v[dimsWithExpressions[dimName]?.name || [dimNameOrAlias]]),
    v => getRepeatedValueToString(v),
    false
  );
  const yAxisValues: string[] = [...new Set(data.map(d => d[relevantDim] as string))];

  const headCellType = hasTimeBucket(dimNameOrAlias)
    ? FormatterType.historicDate
    : dimsWithExpressions[dimName] && activeDimWithExpression
      ? getFieldTypeByExpression(activeDimWithExpression.expression)
      : getHeadCellType({ entityFieldKey: dimNameOrAlias });

  const getCellColorFromData = (value, metric: string): string | undefined => {
    if (featureFlags[FeatureFlags.FormattingRulesDims] && !!dimFormattingRule && isDimensionFormattingRule(dimFormattingRule)) {
      return undefined;
    }

    const metricRule = getFormattingRuleById(
      widget.definition.custom.fieldStyle?.[metric]?.formattingRuleId,
      formattingRules,
      widget?.definition.custom.applyFormattingToAll?.formattingRuleId
    );

    return getColorFromFormattingConditions({
      value,
      formattingRule: metricRule,
    });
  };

  const barDataMultiMetrics: BarDataEntry[] = data?.map(v => ({
    ...v,
    headCellType,
    originalValue: v[activeDimWithExpression ? activeDimWithExpression.name : dimNameOrAlias],
    name: generateInnerDefaultFormatter({
      value: v[activeDimWithExpression ? activeDimWithExpression.name : dimNameOrAlias],
      type: activeDimWithExpression ? getFieldTypeByExpression(activeDimWithExpression.expression) : headCellType,
      fieldName:
        activeDimWithExpression && isDateTruncExpression(activeDimWithExpression.expression)
          ? getDateBucketFromExpression(activeDimWithExpression.expression)
          : dimNameOrAlias,
      shouldDisplayNull,
    }),
  }));

  const getColorFromSeries = ({
    dimValue,
    metricValue,
    metricName,
    index,
    primaryDimValue,
    dimName,
  }: {
    dimValue: string | undefined;
    metricValue?: number;
    index: number;
    metricName: string;
    dimName: string;
    primaryDimValue: string | number | boolean | string[] | null | undefined;
  }): string | undefined => {
    const multiDimFormattingRule = getFormattingRuleById(
      widget.definition.custom.fieldStyle?.[dimName]?.formattingRuleId,
      formattingRules,
      widget?.definition.custom.applyFormattingToAll?.formattingRuleId
    );

    const effectiveDimFormattingRule = barQueryType === BarVisualizationType.MultiDims ? multiDimFormattingRule : dimFormattingRule;

    const effectiveDimValueForFormatting = barQueryType === BarVisualizationType.MultiDims ? dimValue : primaryDimValue;

    if (
      featureFlags[FeatureFlags.FormattingRulesDims] &&
      !!effectiveDimFormattingRule &&
      isDimensionFormattingRule(effectiveDimFormattingRule)
    ) {
      const color = getColorFromFormattingConditions({
        value: effectiveDimValueForFormatting,
        formattingRule: effectiveDimFormattingRule,
      });
      if (color) return color;
    }

    const metricRule = getFormattingRuleById(
      widget.definition.custom.fieldStyle?.[metricName]?.formattingRuleId,
      formattingRules,
      widget?.definition.custom.applyFormattingToAll?.formattingRuleId
    );
    const color = getColorFromFormattingConditions({
      value: metricValue,
      formattingRule: metricRule,
    });

    if (color) {
      return color;
    }
    return isValidSeverity(dimValue) ? SeverityPrimaryColors[dimValue!] : customPalette[index];
  };

  const barDataSingleMetric = (xAxisValue: any): BarDataEntry => {
    const primaryDimKey = activeDimWithExpression ? dimName : dimNameOrAlias;

    const row = data
      ?.filter(item => getRepeatedValueToString(item[primaryDimKey]) === getRepeatedValueToString(xAxisValue))
      .reduce((acc, item) => {
        const seriesValue = isMultiDimBarType || !activeDimWithExpression ? item[relevantDim] : item[activeDimWithExpression.name];

        const dataKey = seriesValue === null ? 'null' : getRepeatedValueToString(seriesValue);

        return {
          ...acc,
          [dataKey]: item[singleMetricName] || 0,
        };
      }, {});

    return {
      name: generateInnerDefaultFormatter({
        value: Array.isArray(xAxisValue) ? getRepeatedValueToString(xAxisValue) : xAxisValue,
        type: activeDimWithExpression ? getFieldTypeByExpression(activeDimWithExpression.expression) : headCellType,
        fieldName:
          activeDimWithExpression && isDateTruncExpression(activeDimWithExpression.expression)
            ? getDateBucketFromExpression(activeDimWithExpression.expression)
            : dimNameOrAlias,
        shouldDisplayNull,
      }),
      originalValue: xAxisValue,
      headCellType,
      ...row,
    };
  };

  const getTotalMetricCount = (bar: BarDataEntry): number =>
    Object.entries(bar).reduce((count: number, [name, value]) => count + (yAxisValues.includes(name) ? (Number(value) ?? 0) : 0), 0);

  const barData: BarDataEntry[] = [BarVisualizationType.MultiDims, BarVisualizationType.Normal].includes(barQueryType)
    ? sortBy?.name === singleMetricName
      ? xAxis0Values
          ?.map(barDataSingleMetric)
          .toSorted((a, b) => (getTotalMetricCount(b) - getTotalMetricCount(a)) * (sortBy.dir === SortDir.ASC ? -1 : 1))
      : xAxis0Values?.map(barDataSingleMetric)
    : barDataMultiMetrics;

  const getDataKey = value => {
    const parsedVal = getRepeatedValueToString(value);

    // Boolean values are not supported as boolean in bar charts
    if (typeof parsedVal === 'boolean') {
      return `${parsedVal}`;
    }

    return parsedVal || 'null';
  };

  const getSingleMetricSeries = () =>
    uniqBy(
      data?.map(datum => {
        const activeDimWithExpression = dimsWithExpressions[dimName];
        const primaryDimKey = activeDimWithExpression ? dimName : dimNameOrAlias;
        const headCellType =
          hasTimeBucket(relevantDim) || (activeDimWithExpression && !isMultiDimBarType)
            ? FormatterType.historicDate
            : getHeadCellType({ entityFieldKey: relevantDim });

        return {
          name: generateInnerDefaultFormatter({
            value: isMultiDimBarType || !activeDimWithExpression ? datum[relevantDim] : datum[activeDimWithExpression.name],
            type:
              !isMultiDimBarType && activeDimWithExpression ? getFieldTypeByExpression(activeDimWithExpression.expression) : headCellType,
            fieldName:
              isMultiDimBarType || !activeDimWithExpression
                ? relevantDim
                : activeDimWithExpression && isDateTruncExpression(activeDimWithExpression.expression)
                  ? getDateBucketFromExpression(activeDimWithExpression.expression)
                  : dimNameOrAlias,
            shouldDisplayNull,
          }),
          metricValue: datum[singleMetricName],
          primaryDimValue: datum[primaryDimKey],
          dataKey: getDataKey(datum[isMultiDimBarType || !activeDimWithExpression ? relevantDim : activeDimWithExpression.name]),
          stackId: isOneBarForXValue ? 'one' : undefined,
          type: 'bar',
          is100Percent: isStacked100,
        };
      }),
      v => v.name
    ).map((v, i) => ({
      ...v,
      color: getColorFromSeries({
        dimValue: v.name,
        metricValue: v.metricValue,
        index: i,
        metricName: singleMetricName,
        dimName: relevantDim,
        primaryDimValue: v.primaryDimValue,
      }),
    }));

  const getColorsFromSeriesMultiMetrics = ({
    metricName,
    index,
    primaryDimValue,
  }: {
    metricName: string | undefined;
    index: number;
    primaryDimValue: string | number | boolean | string[] | null | undefined;
  }): (string | undefined)[] =>
    uniqBy(
      barData.reduce(
        (acc, v) => {
          if (metricName) {
            return [
              ...acc,
              getColorFromSeries({
                dimValue: dimNameOrAlias,
                metricName,
                index,
                metricValue: v[metricName] as number,
                dimName: relevantDim,
                primaryDimValue,
              }),
            ];
          }
          return acc;
        },
        [] as (string | undefined)[]
      ),
      v => v
    );

  const getMultiMetricsSeries = () =>
    uniqBy(
      metrics?.map(({ alias: metricName }, i) => {
        const primaryDimKey = activeDimWithExpression ? dimName : dimNameOrAlias;
        const colors = getColorsFromSeriesMultiMetrics({ metricName, index: i, primaryDimValue: data?.[0]?.[primaryDimKey] });
        return {
          name: getDisplayName(metricName),
          dataKey: metricName,
          stackId: isOneBarForXValue ? 'none' : undefined,
          color: colors.length === 1 ? colors[0] : colors,
          formattingRule:
            metricName &&
            getFormattingRuleById(
              widget.definition.custom.fieldStyle?.[metricName]?.formattingRuleId,
              formattingRules,
              widget?.definition.custom.applyFormattingToAll?.formattingRuleId
            ),
          type: 'bar',
          is100Percent: isStacked100,
        };
      }),
      v => v.name
    );

  const series = barData
    ? [BarVisualizationType.MultiDims, BarVisualizationType.Normal].includes(barQueryType)
      ? getSingleMetricSeries()
      : getMultiMetricsSeries()
    : [];

  const hasFormattingRules =
    (!!widget.definition.custom.applyFormattingToAll?.formattingRuleId &&
      widget.definition.custom.applyFormattingToAll?.formattingRuleId !== NONE_ID) ||
    !!Object.values(widget.definition.custom.fieldStyle || {}).find(v => !!v?.formattingRuleId && v?.formattingRuleId !== NONE_ID);

  const isMultiMeasurement = widget.requests[0].select.metrics.length > 1;

  return {
    barData,
    series,
    isStacked: isStacked || isStacked100,
    hasFormattingRules,
    getCellColorFromData,
    activeDimWithExpression,
    barQueryType,
    tooltipComponentProps: {
      shared: isMultiMeasurement || featureFlags[FeatureFlags.DefaultMinPointSizeBarChart],
    },
    tooltipProps: {
      titleFormatter: t => t || NO_VALUE,
      metricFormatter: v => {
        const headCellType = isMultiDimBarType
          ? getHeadCellType({ entityFieldKey: relevantDim })
          : activeDimWithExpression
            ? FormatterType.historicDate
            : getHeadCellType({ entityFieldKey: relevantDim });
        const fieldName = isMultiDimBarType
          ? relevantDim
          : activeDimWithExpression
            ? getDateBucketFromExpression(activeDimWithExpression.expression)
            : relevantDim;

        return barQueryType === BarVisualizationType.MultiMetric
          ? getDisplayName(v)
          : generateInnerDefaultFormatter({
              value: v,
              type: headCellType,
              fieldName,
              shouldDisplayNull,
            });
      },
    },
  };
};
